import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export type ReportToolTab = 'settings' | 'content';

/**
 * Indhold, then Indstillinger, in the Rapport column. The panel opens on Indhold.
 * Same tab chrome as the journal history column.
 */
export function ReportToolTabs({
  value,
  onChange,
}: {
  value: ReportToolTab;
  onChange: (value: ReportToolTab) => void;
}) {
  const { t } = useTranslation();
  const tabs: { id: ReportToolTab; label: string }[] = [
    { id: 'content', label: t('patientReports.tool_tab_content') },
    { id: 'settings', label: t('patientReports.tool_tab_settings') },
  ];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key !== 'ArrowRight' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'Home' &&
      event.key !== 'End'
    ) {
      return;
    }
    event.preventDefault();
    const index = Math.max(
      0,
      tabs.findIndex((tab) => tab.id === value),
    );
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else next = tabs.length - 1;
    const target = tabs[next];
    if (!target) return;
    onChange(target.id);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons[next]?.focus();
  };

  return (
    <div
      className="jw-tabs"
      role="tablist"
      aria-label={t('patientReports.tool_tabs_label')}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`report-tool-${tab.id}`}
            aria-controls={`report-tool-${tab.id}-panel`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`report-tool-tab-${tab.id}`}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
